<template>
  <div
    class="min-h-[70vh] mt-8 bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50/30 pt-[70px] md:pt-[90px] pb-[60px] md:pb-[100px]"
  >
    <!-- Pattern décoratif en arrière-plan -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute top-10 md:top-20 left-5 md:left-10 w-20 md:w-32 h-20 md:h-32 bg-gradient-to-br from-emerald-200/20 to-teal-200/20 rounded-full blur-xl"
      ></div>
      <div
        class="absolute hidden lg:block right-35 w-90 h-48"
        :class="isAuthenticated ? 'top-30' : 'top-12'"
      >
        <img src="@/assets/img/corner-image.webp" alt="" />
      </div>
      <div
        class="absolute bottom-10 md:bottom-20 left-1/4 md:left-1/3 w-24 md:w-40 h-24 md:h-40 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-xl"
      ></div>
    </div>

    <div class="relative container mx-auto px-4 md:px-6">
      <!-- Navigation améliorée avec breadcrumb -->
      <div class="mb-6 md:mb-10">
        <nav
          class="flex items-center space-x-2 md:space-x-3 text-sm mb-4 md:mb-6"
          v-if="currentSection > 1"
        >
          <!-- Bouton retour stylisé -->
          <button
            @click="goBack"
            class="group flex cursor-pointer items-center justify-center w-10 h-10 md:w-11 md:h-11 bg-gradient-to-r from-primary-400 to-primary-600 hover:from-primary-300 hover:to-primary-500 text-white rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
          >
            <i
              class="fa-solid fa-arrow-left text-sm group-hover:-translate-x-0.5 transition-transform"
            ></i>
          </button>

          <!-- Breadcrumb amélioré -->
          <div class="flex items-center space-x-2 md:space-x-3 overflow-x-auto">
            <button
              v-if="currentSection > 1"
              @click="goBack"
              class="flex items-center space-x-2 bg-white/80 backdrop-blur-sm hover:bg-white px-3 md:px-4 py-2 rounded-xl transition-all duration-200 hover:shadow-md border border-gray-200/50 whitespace-nowrap"
            >
              <div class="w-2 h-2 bg-emerald-400 rounded-full"></div>
              <span
                class="text-gray-700 hover:text-emerald-600 font-medium transition-colors text-sm md:text-base"
              >
                {{ selectedExamType?.name }}
              </span>
            </button>

            <i
              v-if="currentSection > 2"
              class="fa-solid fa-chevron-right text-gray-300 text-xs flex-shrink-0"
            ></i>

            <button
              v-if="currentSection > 2"
              @click="
                () => {
                  currentSection = 2
                  selectedSkill = null
                }
              "
              class="flex items-center space-x-2 bg-white/80 backdrop-blur-sm hover:bg-white px-3 md:px-4 py-2 rounded-xl transition-all duration-200 hover:shadow-md border border-gray-200/50 whitespace-nowrap"
            >
              <div class="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span
                class="text-gray-700 hover:text-blue-600 font-medium transition-colors text-sm md:text-base"
              >
                {{ selectedSkill?.name }}
              </span>
            </button>
          </div>
        </nav>

        <!-- En-tête avec design moderne -->
        <div class="">
          <div class="mb-4">
            <h1
              class="text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 bg-clip-text text-transparent leading-tight"
            >
              {{ getTitle() }}
            </h1>
            <!-- Ligne décorative -->
            <div
              class="w-16 md:w-20 h-1 bg-gradient-to-r from-primary-400 to-primary-500 mt-3 md:mt-4 rounded-full"
            ></div>
          </div>

          <p class="text-base md:text-lg text-gray-600 leading-relaxed">
            {{ getSubtitle() }}
          </p>
        </div>
      </div>

      <!-- États de chargement et d'erreur -->
      <div v-if="isLoading" class="flex items-center justify-center py-8 md:py-12">
        <div
          class="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-blue-500"
        ></div>
      </div>

      <div v-else-if="error" class="text-center py-8 md:py-12">
        <div
          class="w-12 h-12 md:w-16 md:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <i class="fa-solid fa-exclamation-triangle text-red-500 text-xl md:text-2xl"></i>
        </div>
        <p class="text-gray-600 mb-4">{{ error }}</p>
        <button
          @click="retry"
          class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Réessayer
        </button>
      </div>

      <!-- Grille des types d'examen -->
      <div v-else-if="currentSection === 1" class="grid gap-6 md:gap-8 md:grid-cols-2 mx-auto">
        <div
          v-for="examType in examTypes"
          :key="examType.slug"
          class="relative bg-gradient-to-br from-white to-blue-50/30 border border-gray-200 rounded-2xl p-6 md:p-8 cursor-pointer transition-all duration-300 hover:border-blue-300 hover:shadow-2xl hover:-translate-y-2 group overflow-hidden"
          @click="selectExamType(examType)"
        >
          <div
            class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-indigo-500"
          ></div>

          <div
            class="absolute -top-4 -right-4 w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full opacity-50 group-hover:opacity-70 transition-opacity"
          ></div>

          <div class="relative z-10">
            <div class="flex items-start justify-between mb-4 md:mb-6">
              <div class="flex-1">
                <div class="flex items-center space-x-3 mb-3">
                  <div
                    class="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                  >
                    <i :class="examType.icon" class="text-white text-lg md:text-xl"></i>
                  </div>
                  <div>
                    <h3
                      class="text-xl md:text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors"
                    >
                      {{ examType.name }}
                    </h3>
                    <div
                      class="w-0 h-0.5 bg-gradient-to-r from-blue-400 to-indigo-500 group-hover:w-12 md:group-hover:w-16 transition-all duration-500"
                    ></div>
                  </div>
                </div>

                <div
                  class="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-3 md:px-4 py-2 rounded-xl"
                >
                  <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span class="text-xs md:text-sm font-semibold text-blue-700">{{
                    examType.badge
                  }}</span>
                </div>
              </div>

              <div
                class="w-8 h-8 md:w-10 md:h-10 bg-gray-100 group-hover:bg-blue-100 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 flex-shrink-0"
              >
                <i
                  class="fa-solid fa-arrow-right text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all text-sm"
                ></i>
              </div>
            </div>

            <p
              class="text-gray-600 leading-relaxed mb-4 md:mb-6 group-hover:text-gray-700 transition-colors text-sm md:text-base"
            >
              {{ examType.description }}
            </p>

            <div class="flex items-center justify-between flex-wrap gap-2">
              <div class="flex items-center space-x-3 md:space-x-4">
                <div class="flex items-center space-x-2 text-sm">
                  <div
                    class="w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center"
                  >
                    <i class="fa-solid fa-clock text-white text-xs"></i>
                  </div>
                  <span
                    class="text-gray-600 group-hover:text-blue-700 transition-colors font-medium text-xs md:text-sm"
                    >{{ examType.duration }}</span
                  >
                </div>
                <div class="flex items-center space-x-2 text-sm">
                  <div
                    class="w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-lg flex items-center justify-center"
                  >
                    <i class="fa-solid fa-tasks text-white text-xs"></i>
                  </div>
                  <span
                    class="text-gray-600 group-hover:text-blue-700 transition-colors font-medium text-xs md:text-sm"
                    >{{ examType.skills }} compétences</span
                  >
                </div>
              </div>
            </div>
          </div>

          <div
            class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
          ></div>
        </div>
      </div>

      <!-- Grille des compétences -->
      <div
        v-else-if="currentSection === 2"
        class="grid gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-2 mx-auto"
      >
        <div
          v-for="skill in skills"
          :key="skill.slug"
          class="relative bg-gradient-to-br from-white to-emerald-50/30 border border-gray-200 rounded-2xl p-6 md:p-8 cursor-pointer transition-all duration-300 hover:border-emerald-300 hover:shadow-2xl hover:-translate-y-2 group overflow-hidden"
          @click="selectSkill(skill)"
        >
          <div class="absolute top-0 left-0 w-full h-1" :class="skill.gradient"></div>

          <div
            class="absolute -top-4 -right-4 w-16 h-16 md:w-20 md:h-20 rounded-full opacity-50 group-hover:opacity-70 transition-opacity"
            :class="skill.bgGradient"
          ></div>

          <div class="relative z-10">
            <div class="flex items-start justify-between mb-4 md:mb-6">
              <div class="flex-1">
                <div class="flex items-center space-x-3 mb-4">
                  <div
                    class="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                    :class="skill.iconBg"
                  >
                    <i :class="skill.icon" class="text-white text-xl md:text-2xl"></i>
                  </div>
                  <div>
                    <h3
                      class="text-lg md:text-xl font-bold text-gray-900 transition-colors"
                      :class="skill.hoverColor"
                    >
                      {{ skill.name }}
                    </h3>
                    <div
                      class="w-0 h-0.5 group-hover:w-10 md:group-hover:w-12 transition-all duration-500"
                      :class="skill.gradient"
                    ></div>
                  </div>
                </div>

                <div
                  class="inline-flex items-center space-x-2 px-3 py-2 rounded-xl"
                  :class="skill.badgeBg"
                >
                  <div class="w-2 h-2 rounded-full animate-pulse" :class="skill.badgeDot"></div>
                  <span class="text-xs font-semibold" :class="skill.badgeText">{{
                    skill.level
                  }}</span>
                </div>
              </div>

              <div
                class="w-8 h-8 md:w-10 md:h-10 bg-gray-100 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 flex-shrink-0"
                :class="skill.arrowBg"
              >
                <i
                  class="fa-solid fa-arrow-right text-gray-400 group-hover:translate-x-1 transition-all text-sm"
                  :class="skill.arrowColor"
                ></i>
              </div>
            </div>

            <p
              class="text-gray-600 leading-relaxed mb-4 group-hover:text-gray-700 transition-colors text-sm md:text-base"
            >
              {{ skill.description }}
            </p>

            <div class="space-y-2">
              <div
                v-for="point in skill.keyPoints"
                :key="point"
                class="flex items-center space-x-2 text-sm"
              >
                <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="skill.pointColor"></div>
                <span
                  class="text-gray-600 transition-colors text-xs md:text-sm"
                  :class="skill.hoverTextColor"
                  >{{ point }}</span
                >
              </div>
            </div>
          </div>

          <div
            class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
          ></div>
        </div>
      </div>

      <!-- Contenu méthodologique détaillé -->
      <div v-else-if="currentSection === 3" class="max-w-6xl mx-auto">
        <div class="space-y-6 md:space-y-8">
          <!-- Section Vidéo -->
          <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div class="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 md:p-6">
              <div class="flex items-center space-x-3">
                <div
                  class="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-xl flex items-center justify-center"
                >
                  <i class="fa-solid fa-video text-white text-lg md:text-xl"></i>
                </div>
                <div>
                  <h2 class="text-xl md:text-2xl font-bold text-white">Vidéo de Méthodologie</h2>
                  <p class="text-blue-100 text-sm md:text-base">
                    {{ selectedSkill.name }} - Guide complet
                  </p>
                </div>
              </div>
            </div>

            <div class="p-4 md:p-8">
              <div
                class="relative aspect-video bg-gray-900 rounded-xl overflow-hidden mb-4 md:mb-6"
              >
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="text-center px-4">
                    <div
                      class="w-16 h-16 md:w-20 md:h-20 bg-white/20 rounded-full flex items-center justify-center mb-4 mx-auto"
                    >
                      <i class="fa-solid fa-play text-white text-xl md:text-2xl ml-1"></i>
                    </div>
                    <p class="text-white text-base md:text-lg font-semibold">
                      {{ selectedSkill.videoTitle }}
                    </p>
                    <p class="text-gray-300 text-sm md:text-base">
                      Durée: {{ selectedSkill.videoDuration }}
                    </p>
                  </div>
                </div>
              </div>

              <p class="text-gray-600 leading-relaxed text-sm md:text-base">
                {{ selectedSkill.videoDescription }}
              </p>
            </div>
          </div>

          <!-- Section Méthodologie Textuelle -->
          <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div class="bg-gradient-to-r from-emerald-500 to-teal-600 p-4 md:p-6">
              <div class="flex items-center space-x-3">
                <div
                  class="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-xl flex items-center justify-center"
                >
                  <i class="fa-solid fa-book-open text-white text-lg md:text-xl"></i>
                </div>
                <div>
                  <h2 class="text-xl md:text-2xl font-bold text-white">Méthodologie Détaillée</h2>
                  <p class="text-emerald-100 text-sm md:text-base">
                    Stratégies et techniques éprouvées
                  </p>
                </div>
              </div>
            </div>

            <div class="p-4 md:p-8">
              <div class="prose prose-sm md:prose-lg max-w-none">
                <div
                  v-for="(section, index) in selectedSkill.methodology"
                  :key="index"
                  class="mb-6 md:mb-8"
                >
                  <h3
                    class="text-lg md:text-xl font-bold text-gray-900 mb-4 flex items-start space-x-3"
                  >
                    <div
                      class="w-7 h-7 md:w-8 md:h-8 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    >
                      <span class="text-white text-sm font-bold">{{ index + 1 }}</span>
                    </div>
                    <span>{{ section.title }}</span>
                  </h3>

                  <div class="ml-10 md:ml-11">
                    <p class="text-gray-600 leading-relaxed mb-4 text-sm md:text-base">
                      {{ section.description }}
                    </p>

                    <div v-if="section.strategies" class="space-y-3">
                      <h4 class="font-semibold text-gray-800 mb-3 text-sm md:text-base">
                        Stratégies clés :
                      </h4>
                      <ul class="space-y-2">
                        <li
                          v-for="strategy in section.strategies"
                          :key="strategy"
                          class="flex items-start space-x-3"
                        >
                          <div class="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span class="text-gray-600 text-sm md:text-base">{{ strategy }}</span>
                        </li>
                      </ul>
                    </div>

                    <div
                      v-if="section.tips"
                      class="mt-4 md:mt-6 bg-blue-50 border-l-4 border-blue-400 p-3 md:p-4 rounded-r-lg"
                    >
                      <h5
                        class="font-semibold text-blue-800 mb-2 flex items-center space-x-2 text-sm md:text-base"
                      >
                        <i class="fa-solid fa-lightbulb"></i>
                        <span>Conseils pratiques</span>
                      </h5>
                      <ul class="space-y-1">
                        <li
                          v-for="tip in section.tips"
                          :key="tip"
                          class="text-blue-700 text-xs md:text-sm"
                        >
                          • {{ tip }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section Erreurs Communes -->
          <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div class="bg-gradient-to-r from-red-500 to-pink-600 p-4 md:p-6">
              <div class="flex items-center space-x-3">
                <div
                  class="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-xl flex items-center justify-center"
                >
                  <i class="fa-solid fa-exclamation-triangle text-white text-lg md:text-xl"></i>
                </div>
                <div>
                  <h2 class="text-xl md:text-2xl font-bold text-white">Erreurs à Éviter</h2>
                  <p class="text-red-100 text-sm md:text-base">Les pièges les plus fréquents</p>
                </div>
              </div>
            </div>

            <div class="p-4 md:p-8">
              <div class="grid gap-4 md:gap-6 md:grid-cols-2">
                <div
                  v-for="error in selectedSkill.commonErrors"
                  :key="error.title"
                  class="bg-red-50 border border-red-200 rounded-xl p-4 md:p-6"
                >
                  <div class="flex items-start space-x-3">
                    <div
                      class="w-7 h-7 md:w-8 md:h-8 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0"
                    >
                      <i class="fa-solid fa-times text-white text-xs md:text-sm"></i>
                    </div>
                    <div>
                      <h4 class="font-bold text-red-800 mb-2 text-sm md:text-base">
                        {{ error.title }}
                      </h4>
                      <p class="text-red-700 text-xs md:text-sm mb-3">{{ error.description }}</p>
                      <div class="bg-white border border-red-200 rounded-lg p-3">
                        <p
                          class="text-green-700 text-xs md:text-sm font-medium flex items-center space-x-2"
                        >
                          <i class="fa-solid fa-check text-green-500"></i>
                          <span>{{ error.solution }}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="w-full pb-6 md:pb-8 container">
    <div
      class="grid mx-auto grid-cols-2 lg:grid-cols-4 bg-[#92d0f5] text-gray-600 rounded-xl py-4 md:py-6 gap-4 lg:gap-0"
    >
      <div
        class="flex flex-col items-center justify-center p-3 md:p-4 border-b lg:border-b-0 lg:border-r border-white"
      >
        <span class="text-xl md:text-2xl font-bold">+10 000</span>
        <span class="text-xs md:text-sm text-gray-600 mt-1 text-center">Étudiants inscrits</span>
      </div>

      <div
        class="flex flex-col items-center justify-center p-3 md:p-4 border-b lg:border-b-0 lg:border-r border-white"
      >
        <span class="text-xl md:text-2xl font-bold">+100</span>
        <span class="text-xs md:text-sm text-gray-600 mt-1 text-center">Exercices pratiques</span>
      </div>

      <div class="flex flex-col items-center justify-center p-3 md:p-4 lg:border-r border-white">
        <span class="text-xl md:text-2xl font-bold">+95%</span>
        <span class="text-xs md:text-sm text-gray-600 mt-1 text-center">De réussite au TCF</span>
      </div>

      <div class="flex flex-col items-center justify-center p-3 md:p-4">
        <span class="text-xl md:text-2xl font-bold">+3500</span>
        <span class="text-xs md:text-sm text-gray-600 mt-1 text-center">Questions</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// Router et route
const route = useRoute()
const router = useRouter()

// State management
const currentSection = ref(1) // 1: exam types, 2: skills, 3: content
const selectedExamType = ref(null)
const selectedSkill = ref(null)
const isLoading = ref(false)
const error = ref(null)
const isAuthenticated = ref(true) // Simulated

// Données en dur pour TCF Canada
const examTypes = ref([
  {
    slug: 'tcf-canada',
    name: 'TCF Canada',
    description:
      "Test de Connaissance du Français pour l'immigration au Canada. Évaluation complète des 4 compétences linguistiques selon les standards canadiens.",
    icon: 'fa-brands fa-canadian-maple-leaf',
    badge: 'Immigration Canada',
    duration: '3h15',
    skills: 4,
  },
])

// Computed properties
const currentModule = computed(() => route.params.module)
const currentLesson = computed(() => route.params.lesson)

// Initialisation basée sur les paramètres de route
const initializeFromRoute = () => {
  const moduleSlug = currentModule.value
  const lessonSlug = currentLesson.value

  if (moduleSlug && lessonSlug) {
    // On est sur une leçon spécifique (/methodologie/:module/:lesson)
    const examType = examTypes.value.find((exam) => exam.slug === moduleSlug)
    const skill = skills.value.find((skill) => skill.slug === lessonSlug)

    if (examType && skill) {
      selectedExamType.value = examType
      selectedSkill.value = skill
      currentSection.value = 3
    } else {
      // Redirection si les paramètres sont invalides
      router.push('/methodologie')
    }
  } else if (moduleSlug) {
    // On est sur un module (/methodologie/:module)
    const examType = examTypes.value.find((exam) => exam.slug === moduleSlug)

    if (examType) {
      selectedExamType.value = examType
      selectedSkill.value = null
      currentSection.value = 2
    } else {
      // Redirection si le module n'existe pas
      router.push('/methodologie')
    }
  } else {
    // On est sur la page principale (/methodologie)
    selectedExamType.value = null
    selectedSkill.value = null
    currentSection.value = 1
  }
}

// Methods
const getTitle = () => {
  switch (currentSection.value) {
    case 1:
      return 'Méthodologie des Examens'
    case 2:
      return `Méthodologie - ${selectedExamType.value?.name}`
    case 3:
      return `${selectedSkill.value?.name} - ${selectedExamType.value?.name}`
    default:
      return 'Méthodologie'
  }
}

const getSubtitle = () => {
  switch (currentSection.value) {
    case 1:
      return "Choisissez l'examen pour lequel vous souhaitez découvrir la méthodologie"
    case 2:
      return 'Sélectionnez la compétence que vous voulez approfondir'
    case 3:
      return 'Méthodologie détaillée avec vidéo explicative et stratégies éprouvées'
    default:
      return ''
  }
}

const selectExamType = (examType) => {
  // Navigation vers /methodologie/:module
  router.push(`/methodologie/${examType.slug}`)
}

const selectSkill = (skill) => {
  // Navigation vers /methodologie/:module/:lesson
  if (selectedExamType.value) {
    router.push(`/methodologie/${selectedExamType.value.slug}/${skill.slug}`)
  }
}

const goBack = () => {
  if (currentSection.value === 3) {
    // Retour vers /methodologie/:module
    if (selectedExamType.value) {
      router.push(`/methodologie/${selectedExamType.value.slug}`)
    }
  } else if (currentSection.value === 2) {
    // Retour vers /methodologie
    router.push('/methodologie')
  }
}

const retry = () => {
  error.value = null
  isLoading.value = false
}

// Navigation directe vers une compétence spécifique
const navigateToSkill = (examTypeSlug, skillSlug) => {
  router.push(`/methodologie/${examTypeSlug}/${skillSlug}`)
}

// Navigation vers la liste des compétences d'un examen
const navigateToExam = (examTypeSlug) => {
  router.push(`/methodologie/${examTypeSlug}`)
}

// Watchers pour réagir aux changements de route
watch(
  () => route.params,
  () => {
    initializeFromRoute()
  },
  { deep: true },
)

// Lifecycle
onMounted(() => {
  initializeFromRoute()
})

const skills = ref([
  {
    slug: 'comprehension-orale',
    name: 'Méthodologie de Compréhension Orale',
    description:
      "Maîtrisez les techniques d'écoute active pour comprendre le français parlé dans les divers documents audio du TCF Canada, des annonces publiques aux chroniques radio.",
    level: 'Niveau A1-C2',
    icon: 'fa-solid fa-headphones',
    gradient: 'bg-gradient-to-r from-blue-400 to-indigo-500',
    bgGradient: 'bg-gradient-to-br from-blue-100 to-indigo-100',
    iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    hoverColor: 'group-hover:text-blue-600',
    badgeBg: 'bg-gradient-to-r from-blue-100 to-indigo-100',
    badgeDot: 'bg-blue-500',
    badgeText: 'text-blue-700',
    arrowBg: 'group-hover:bg-blue-100',
    arrowColor: 'group-hover:text-blue-500',
    hoverTextColor: 'group-hover:text-blue-700',
    pointColor: 'bg-blue-400',
    keyPoints: ['Écoute globale', "Repérage d'indices", 'Inférence et déduction'],
    videoTitle: 'Stratégies pour la Compréhension Orale TCF',
    videoDuration: '15 min',
    videoDescription:
      'Apprenez à anticiper le contenu des pistes audio, à identifier les informations clés et à éviter les pièges fréquents.',
    methodology: [
      {
        title: 'Anticipation et écoute globale',
        description:
          'Avant même la première écoute, préparez-vous en analysant la question. La première écoute sert à saisir le contexte général.',
        strategies: [
          "Lisez la question et les options de réponse avant le début de l'audio",
          'Identifiez les mots-clés dans les questions (Qui ? Quoi ? Où ? Quand ?...)',
          'Lors de la première écoute, concentrez-vous sur la situation globale : qui parle à qui, et de quoi ?',
          'Repérez le ton (informatif, publicitaire, amical...) et les bruits de fond pour des indices de contexte.',
        ],
        tips: [
          'Ne cherchez pas à tout comprendre mot pour mot',
          'Faites une hypothèse sur la bonne réponse dès la première écoute',
          "Restez concentré, les pistes audio sont courtes et ne sont jouées qu'une seule fois.",
        ],
      },
      {
        title: 'Écoute sélective et validation',
        description:
          'Ciblez précisément les informations demandées pour confirmer ou infirmer votre hypothèse initiale.',
        strategies: [
          'Focalisez votre attention sur les mots-clés de la question que vous avez identifiés',
          'Écoutez attentivement les chiffres, les noms, les dates et les lieux',
          'Soyez attentif aux connecteurs logiques (mais, donc, parce que...) qui structurent le discours',
          "Validez votre choix final en vous assurant qu'il correspond bien à ce qui a été dit.",
        ],
        tips: [
          "Attention aux distracteurs : des options de réponse peuvent contenir des mots de l'enregistrement mais être fausses",
          'La bonne réponse est souvent une paraphrase, pas une répétition mot pour mot',
          'Si vous hésitez, éliminez les réponses clairement incorrectes et choisissez la plus probable.',
        ],
      },
      {
        title: 'Inférence et compréhension fine',
        description:
          'Pour les questions de niveaux B2 à C2, il faut souvent déduire des informations qui ne sont pas dites explicitement.',
        strategies: [
          "Analysez le ton, l'intonation et les hésitations pour comprendre les sentiments ou l'opinion du locuteur",
          'Déduisez les relations entre les personnes (collègues, amis, famille...)',
          "Comprenez l'implicite : ce que le locuteur veut dire sans le formuler directement",
          "Identifiez l'objectif du message (convaincre, informer, s'excuser...).",
        ],
        tips: [
          'Faites confiance à votre intuition basée sur le contexte général',
          'Méfiez-vous des réponses trop littérales pour des questions complexes',
          "La culture générale peut aider, mais la réponse doit toujours se baser sur l'enregistrement.",
        ],
      },
    ],
    commonErrors: [
      {
        title: 'Se laisser distraire par un mot inconnu',
        description: "Perdre le fil de l'écoute en se focalisant sur un mot qu'on ne comprend pas.",
        solution:
          'Ignorez le mot inconnu et concentrez-vous sur le reste de la phrase et le contexte global.',
      },
      {
        title: 'Choisir une réponse basée sur un seul mot entendu',
        description:
          "Tomber dans le piège d'un mot présent dans l'audio et dans une mauvaise réponse.",
        solution:
          "Vérifiez que le sens global de la réponse correspond à l'enregistrement, pas juste un mot.",
      },
      {
        title: 'Ne pas lire la question en premier',
        description: 'Écouter "à l\'aveugle" sans savoir quelle information rechercher.',
        solution:
          "Utilisez toujours les quelques secondes avant l'audio pour lire la question et anticiper.",
      },
      {
        title: 'Paniquer et perdre sa concentration',
        description:
          "Rater une partie de l'enregistrement à cause du stress et abandonner la question.",
        solution:
          'Restez calme. Si vous avez manqué une information, fiez-vous au sens général pour déduire la réponse.',
      },
    ],
  },
  {
    slug: 'comprehension-ecrite',
    name: 'Méthodologie de Compréhension Écrite',
    description:
      'Développez votre capacité à lire et comprendre rapidement divers types de documents écrits, des articles de presse aux petites annonces.',
    level: 'Niveau A1-C2',
    icon: 'fa-solid fa-book-open',
    gradient: 'bg-gradient-to-r from-emerald-400 to-cyan-500',
    bgGradient: 'bg-gradient-to-br from-emerald-100 to-cyan-100',
    iconBg: 'bg-gradient-to-br from-emerald-500 to-cyan-600',
    hoverColor: 'group-hover:text-emerald-600',
    badgeBg: 'bg-gradient-to-r from-emerald-100 to-cyan-100',
    badgeDot: 'bg-emerald-500',
    badgeText: 'text-emerald-700',
    arrowBg: 'group-hover:bg-emerald-100',
    arrowColor: 'group-hover:text-emerald-500',
    hoverTextColor: 'group-hover:text-emerald-700',
    pointColor: 'bg-emerald-400',
    keyPoints: ['Survol rapide', 'Lecture ciblée', 'Analyse structurelle'],
    videoTitle: 'Techniques de Compréhension Écrite TCF',
    videoDuration: '12 min',
    videoDescription:
      'Apprenez à décoder efficacement les textes du TCF Canada et à identifier rapidement les informations clés.',
    methodology: [
      {
        title: 'Lecture de survol (Skimming)',
        description:
          "Obtenez une vue d'ensemble du texte en moins d'une minute pour identifier sa nature, son thème et sa structure.",
        strategies: [
          'Lisez en premier le titre, les sous-titres et regardez les images ou graphiques',
          'Identifiez la source et le type de document (article, email, publicité, menu...)',
          "Lisez la première et la dernière phrase de chaque paragraphe pour saisir l'idée principale",
          'Repérez les mots en gras, les noms propres et les dates.',
        ],
        tips: [
          "Ne lisez jamais le texte en détail avant d'avoir lu les questions",
          'Formulez une hypothèse sur le sujet général du texte',
          'Cette étape vous permet de savoir où chercher les informations plus tard.',
        ],
      },
      {
        title: 'Lecture ciblée (Scanning)',
        description:
          'Après avoir lu les questions, retournez au texte pour localiser rapidement les informations spécifiques nécessaires pour répondre.',
        strategies: [
          'Lisez attentivement chaque question et surlignez les mots-clés',
          'Scannez le texte pour retrouver ces mots-clés ou leurs synonymes',
          'Une fois le mot-clé trouvé, lisez attentivement la phrase complète qui le contient',
          "Comparez l'information trouvée avec les options de réponse.",
        ],
        tips: [
          "Les questions suivent presque toujours l'ordre chronologique du texte",
          'Soyez très attentif aux synonymes et aux reformulations, qui sont fréquents',
          'Les connecteurs logiques (ex: "en revanche", "par conséquent") sont des indicateurs précieux.',
        ],
      },
      {
        title: 'Analyse et déduction',
        description:
          "Pour les questions complexes, il faut comprendre le non-dit, l'opinion de l'auteur et la structure logique du texte.",
        strategies: [
          "Identifiez la différence entre un fait objectif et une opinion de l'auteur",
          "Déduisez le sens d'un mot inconnu grâce au contexte de la phrase",
          "Analysez les relations de cause à effet, d'opposition ou de comparaison",
          "Comprenez l'objectif global du texte (critiquer, informer, persuader...).",
        ],
        tips: [
          'Méfiez-vous des réponses qui reprennent des mots du texte mais en changent le sens',
          'Éliminez les réponses qui sont des généralisations abusives ou des affirmations trop fortes',
          'Votre réponse doit être justifiée à 100% par le texte, pas par vos connaissances personnelles.',
        ],
      },
    ],
    commonErrors: [
      {
        title: 'Lire le texte en entier avant les questions',
        description:
          'Perdre un temps précieux à lire des détails qui ne seront utiles pour aucune question.',
        solution:
          'Lisez toujours les questions en premier pour savoir quoi chercher et guider votre lecture.',
      },
      {
        title: 'Se fier uniquement aux mots identiques',
        description:
          "Choisir une réponse parce qu'elle contient le même mot que le texte, sans vérifier le sens.",
        solution: 'Recherchez le sens et les idées. Privilégiez les paraphrases et les synonymes.',
      },
      {
        title: 'Faire des suppositions personnelles',
        description:
          "Utiliser ses propres connaissances ou opinions pour répondre, au lieu de s'en tenir strictement au texte.",
        solution:
          'Toute réponse doit être prouvée par une phrase ou une idée présente dans le document.',
      },
      {
        title: 'Gérer son temps de manière inefficace',
        description:
          'Passer beaucoup trop de temps sur un texte difficile et manquer de temps pour les plus faciles.',
        solution:
          'Fixez-vous une limite de temps par document. Si vous êtes bloqué, passez au suivant et revenez-y plus tard.',
      },
    ],
  },
  {
    slug: 'expression-ecrite',
    name: "Méthodologie de l'Expression Écrite",
    description:
      "Apprenez à structurer vos idées pour les 3 tâches de l'épreuve : de la description d'un quotidien à la rédaction d'un texte argumenté complexe.",
    level: 'Niveau A2-C2',
    icon: 'fa-solid fa-pen-fancy',
    gradient: 'bg-gradient-to-r from-purple-400 to-pink-500',
    bgGradient: 'bg-gradient-to-br from-purple-100 to-pink-100',
    iconBg: 'bg-gradient-to-br from-purple-500 to-pink-600',
    hoverColor: 'group-hover:text-purple-600',
    badgeBg: 'bg-gradient-to-r from-purple-100 to-pink-100',
    badgeDot: 'bg-purple-500',
    badgeText: 'text-purple-700',
    arrowBg: 'group-hover:bg-purple-100',
    arrowColor: 'group-hover:text-purple-500',
    hoverTextColor: 'group-hover:text-purple-700',
    pointColor: 'bg-purple-400',
    keyPoints: ['Analyse de la consigne', 'Structure par tâche', 'Richesse de la langue'],
    videoTitle: "Maîtriser les 3 Tâches de l'Expression Écrite",
    videoDuration: '18 min',
    videoDescription:
      "Découvrez la structure et les attendus pour chaque tâche de l'épreuve écrite afin de maximiser votre note.",
    methodology: [
      {
        title: 'Tâche 1 : Description / Narration (~60-120 mots)',
        description:
          "Il s'agit souvent de décrire une situation, des personnes, un lieu ou de raconter un événement dans un format court (ex: email, post-it).",
        strategies: [
          'Identifiez clairement le but : décrire, raconter, inviter, etc.',
          'Utilisez un vocabulaire simple et précis lié à la vie quotidienne',
          'Structurez avec des phrases simples et des connecteurs de base (et, puis, alors)',
          'Respectez le format demandé (ex: formule de politesse pour un email).',
        ],
        tips: [
          'Concentrez-vous sur la clarté et la correction grammaticale de base',
          'Répondez à toutes les parties de la consigne (ex: "décrivez le lieu ET dites ce que vous avez fait")',
          'Le registre est souvent informel ou neutre (tutoiement ou vouvoiement selon la consigne).',
        ],
      },
      {
        title: 'Tâche 2 : Récit / Justification (~120-150 mots)',
        description:
          'Vous devez rédiger un texte plus long pour un destinataire précis (lettre, article, courriel) en justifiant une opinion ou en racontant une expérience.',
        strategies: [
          'Analysez le destinataire pour adapter le registre de langue (formel/informel)',
          'Structurez en paragraphes : introduction, développement, conclusion',
          'Donnez votre opinion ou racontez votre expérience en apportant des justifications et des exemples',
          'Utilisez des connecteurs logiques plus variés (car, donc, en effet, cependant).',
        ],
        tips: [
          "Faites un plan rapide sur brouillon avant d'écrire",
          'Variez les structures de phrases pour montrer un niveau plus avancé',
          'Respectez scrupuleusement le nombre de mots demandé.',
        ],
      },
      {
        title: 'Tâche 3 : Texte comparatif et argumenté (~150-180 mots)',
        description:
          'La tâche la plus complexe : présenter deux points de vue sur un sujet de société, puis donner et justifier votre propre opinion de manière nuancée.',
        strategies: [
          'Introduction : reformulez le sujet et annoncez le plan',
          'Partie 1 : Présentez le premier point de vue de manière objective',
          'Partie 2 : Présentez le second point de vue, souvent en opposition',
          'Conclusion : Exprimez votre propre opinion en la nuançant et en la justifiant clairement',
          "Utilisez un vocabulaire riche et des connecteurs d'argumentation (d'une part/d'autre part, en revanche, néanmoins, en conclusion).",
        ],
        tips: [
          'Restez neutre en présentant les deux premiers points de vue',
          'Votre opinion personnelle ne doit apparaître que dans la dernière partie',
          "La qualité de l'argumentation et la structure sont primordiales ici.",
        ],
      },
    ],
    commonErrors: [
      {
        title: 'Ne pas respecter la consigne ou le format',
        description:
          "Écrire un article de blog au lieu d'un email formel, ou oublier une partie de la question.",
        solution:
          'Surlignez les mots-clés de la consigne (type de texte, destinataire, points à aborder) avant de commencer.',
      },
      {
        title: 'Manquer de structure',
        description: 'Rédiger un bloc de texte sans paragraphes clairs ni connecteurs logiques.',
        solution:
          'Faites un plan, même très simple. Utilisez les connecteurs pour guider le lecteur.',
      },
      {
        title: 'Donner son opinion dans toutes les parties (Tâche 3)',
        description:
          'Mélanger son point de vue personnel avec la présentation objective des deux documents.',
        solution:
          'Séparez clairement les parties : 1) Point de vue A, 2) Point de vue B, 3) Mon opinion.',
      },
      {
        title: 'Négliger la relecture',
        description:
          "Laisser des fautes d'inattention (accords, conjugaisons) qui pénalisent la note.",
        solution:
          'Gardez obligatoirement 5-10 minutes à la fin pour relire chaque texte spécifiquement pour corriger les erreurs.',
      },
    ],
  },
  {
    slug: 'expression-orale',
    name: "Méthodologie de l'Expression Orale",
    description:
      "Développez votre aisance à l'oral pour réussir les 3 tâches de l'entretien : de la présentation simple à la défense argumentée d'un point de vue.",
    level: 'Niveau A2-C2',
    icon: 'fa-solid fa-microphone',
    gradient: 'bg-gradient-to-r from-orange-400 to-red-500',
    bgGradient: 'bg-gradient-to-br from-orange-100 to-red-100',
    iconBg: 'bg-gradient-to-br from-orange-500 to-red-600',
    hoverColor: 'group-hover:text-orange-600',
    badgeBg: 'bg-gradient-to-r from-orange-100 to-red-100',
    badgeDot: 'bg-orange-500',
    badgeText: 'text-orange-700',
    arrowBg: 'group-hover:bg-orange-100',
    arrowColor: 'group-hover:text-orange-500',
    hoverTextColor: 'group-hover:text-orange-700',
    pointColor: 'bg-orange-400',
    keyPoints: ['Interaction spontanée', 'Jeu de rôle', 'Argumentation structurée'],
    videoTitle: "Techniques d'Expression Orale TCF",
    videoDuration: '20 min',
    videoDescription:
      "Maîtrisez la structure de chaque tâche orale et apprenez à gérer votre temps de parole et votre stress face à l'examinateur.",
    methodology: [
      {
        title: 'Tâche 1 : Entretien de présentation (Sans préparation)',
        description:
          "L'examinateur vous pose des questions sur vous, votre environnement, vos goûts. Le but est d'établir une conversation simple.",
        strategies: [
          'Répondez aux questions de manière naturelle et développée (évitez "oui"/"non")',
          'Donnez des détails et des exemples pour enrichir vos réponses',
          'Posez au moins une question en retour pour montrer votre capacité à interagir',
          "Utilisez le présent de l'indicatif et le passé composé/imparfait.",
        ],
        tips: [
          "Soyez souriant et détendu, c'est un échange pour vous mettre à l'aise",
          'Préparez des phrases types pour parler de vous (travail, loisirs, famille)',
          'Écoutez bien la question pour y répondre précisément.',
        ],
      },
      {
        title: 'Tâche 2 : Jeu de rôle en situation formelle (Avec préparation)',
        description:
          "Vous devez obtenir des informations auprès de l'examinateur dans une situation de la vie courante (ex: se renseigner sur un cours, faire une réclamation).",
        strategies: [
          'Utilisez le temps de préparation pour lister les questions que vous devez poser',
          'Adoptez un ton formel et utilisez systématiquement le "vous"',
          'Commencez par une formule de politesse ("Bonjour, je vous appelle pour...")',
          'Posez des questions claires et variées (ouvertes, fermées) pour obtenir les informations.',
          'Terminez par une formule de politesse ("Je vous remercie. Au revoir").',
        ],
        tips: [
          'Votre but est de mener la conversation. Soyez proactif.',
          'Montrez que vous comprenez les réponses en réagissant ("Ah, très bien", "D\'accord, je vois").',
          'N\'hésitez pas à demander de répéter si vous ne comprenez pas ("Pourriez-vous répéter, s\'il vous plaît ?").',
        ],
      },
      {
        title: "Tâche 3 : Expression d'un point de vue (Avec préparation)",
        description:
          'Vous devez donner votre opinion sur un sujet de société simple, souvent introduit par une citation ou une question.',
        strategies: [
          'Utilisez la préparation pour bâtir un mini-plan : Introduction, Argument 1 + exemple, Argument 2 + exemple, Conclusion',
          'Introduction : Reformulez le sujet et annoncez votre opinion',
          'Développement : Présentez vos arguments de manière claire en utilisant des connecteurs logiques (Premièrement, De plus, Cependant...)',
          'Conclusion : Résumez votre opinion et ouvrez éventuellement le débat',
          "Soyez prêt à répondre aux questions de l'examinateur pour défendre votre point de vue.",
        ],
        tips: [
          'Parlez pendant environ 3 minutes pour votre exposé initial',
          "Il n'y a pas de bonne ou de mauvaise opinion, c'est la qualité de l'argumentation qui compte",
          "Essayez d'utiliser un vocabulaire plus soutenu et des structures de phrases complexes (subjonctif, conditionnel).",
        ],
      },
    ],
    commonErrors: [
      {
        title: 'Réponses trop courtes ou monosyllabiques',
        description:
          'Répondre par "oui", "non", "je ne sais pas", ce qui ne démontre aucune compétence linguistique.',
        solution:
          'Développez toujours vos réponses. Utilisez la technique "Point-Explication-Exemple".',
      },
      {
        title: 'Ne pas utiliser le temps de préparation efficacement',
        description: 'Se lancer sans avoir structuré ses idées pour les tâches 2 et 3.',
        solution:
          'Utilisez le papier fourni pour noter un plan avec des mots-clés, pas des phrases complètes.',
      },
      {
        title: 'Se bloquer après une erreur de langue',
        description:
          "S'arrêter, s'excuser longuement et perdre le fil de son discours à cause d'une faute.",
        solution:
          'Ignorez les petites erreurs et continuez. La fluidité et la communication sont plus importantes que la perfection.',
      },
      {
        title: "Manquer d'interaction",
        description:
          "Réciter un monologue sans tenir compte de l'examinateur, ou ne poser aucune question en retour.",
        solution:
          "Regardez l'examinateur, réagissez à ce qu'il dit, et n'oubliez pas de poser des questions (surtout Tâche 1 & 2).",
      },
    ],
  },
])
</script>
